import { ComponentSize, LoaderType } from '@eleventheye/asui';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ConsoleID, getConsoleState } from 'app/config/Logger';
import { Cls, Emojis } from 'app/constants';
import { CategoryRankingsNavBarState } from 'app/scenes/statsmanager/components/categoryrankings/components/statsnav/CategoryRankingsNavBar';
import { UserAuthState } from 'app/slices/UserSlice';
import { STAT_CATEGORIES_ABBR } from 'constants/categories-constants';
import { getDbVersion, MY_NFL_STATS_DB } from 'lib/idb/IndexDb';
import {
  AdminAction,
  AdminActionWithAuditLogState,
  AppScene,
  AppThunk,
  AppUIProps,
  CategoryGroupName,
  CategoryRankingsKind,
  CategoryRankingsKindType,
  GameID,
  GameWeek,
  IWorkerIDBAuditMessage,
  LoaderDetails,
  OffensiveGroup,
  RootState,
  Scene,
  SeasonalGameIDMap,
  SeasonID,
  SeasonType,
  SeasonWeekID,
  StatCategoryAbrv,
  WebWorkerType,
} from 'types';

const CONSOLE_ON = getConsoleState(ConsoleID.AppUISlice);

// Memoized at module level - created once and reused
export const timeNavigatorVisibleScenes: Readonly<Partial<Scene[]>> = [
  AppScene.WeeklyGames,
  AppScene.Standings,
  AppScene.Teams,
  AppScene.TeamDetails,
  AppScene.CategoryRankings,
] as const;

const initialState: AppUIProps = {
  isIndexDBInitialized: false,
  routeToLoadAfterAuth: '/',
  applicationInitialized: false,
  showAuthentication: true,
  showUILibrary: false,
  showStarterScreen: false,
  showBackButton: false,
  showTimeNavigator: false,
  modalVisible: false,
  displayCode: false,
  showAdminActions: false,
  categoryRankingsNavBarState: {
    selectedKind: CategoryRankingsKind.Offense as CategoryRankingsKindType,
    selectedGroup: OffensiveGroup.Totals as CategoryGroupName,
    selectedCategory: STAT_CATEGORIES_ABBR.totalDrives,
  },
  loader: {
    visible: false,
    size: ComponentSize.Large,
    type: LoaderType.Spin,
    isModal: true,
  },
  admin: {
    adminAction: {
      action: AdminAction.Idle,
      year: 2025,
      week: GameWeek.Week1,
      gameIDs: [],
      season: SeasonType.Regular,
      modifiedOn: undefined,
    },
    gameIDsMap: {},
  },
  webworker: {
    isEnabled: true,
    status: 'online',
  },
};

export type WeeklyGameIDsPayload = {
  yearID: SeasonID;
  seasonWeekID: SeasonWeekID;
  gameIDs: GameID[];
};

const getResultGetStoreCountMsg = (count: any): string => {
  const msg = `stores record count %c ${count} %c`;
  return `${Emojis.checkMark} ${msg} communication with web worker %c COMPLETE %c`;
};

export const initializeWebWorkerDBAuditor = (): AppThunk => (dispatch) => {
  if (typeof window !== 'undefined' && window.Worker) {
    const webWorkerDbAuditor: Worker = new Worker(new URL('webworkers/webworkerAuditIndexDb.ts', import.meta.url));

    webWorkerDbAuditor.onmessage = (e: MessageEvent<IWorkerIDBAuditMessage>) => {
      const msg0 = `${Emojis.mail} from worker %c ${e.data.type} %c to %c AppUI %c`;
      if (CONSOLE_ON) console.log(msg0, Cls.Info, '', Cls.Loc, '');
      switch (e.data.type) {
        case 'result-get-stores-count':
          if (CONSOLE_ON) console.log(`${Emojis.hammers} stores count %c ${e.data.payload} %c`, Cls.ValLg, '');
          webWorkerDbAuditor.postMessage({
            type: 'get-stores-names',
            request: { idbName: MY_NFL_STATS_DB, idbVersion: getDbVersion() },
          } as IWorkerIDBAuditMessage);
          break;
        case 'result-get-stores-names':
          if (CONSOLE_ON) console.log(`${Emojis.hammers} store names %c ${e.data.payload} %c`, Cls.ValLg, '');
          webWorkerDbAuditor.postMessage({
            type: 'get-stores-records-count',
            request: { idbName: MY_NFL_STATS_DB, idbVersion: getDbVersion() },
          } as IWorkerIDBAuditMessage);
          break;
        case 'result-get-stores-records-count':
          if (CONSOLE_ON) console.log(getResultGetStoreCountMsg(e.data.payload), Cls.Val, '', Cls.Army, '');
          dispatch(setWebWorker({ status: 'COMPLETE' }));
          break;
        default:
          if (CONSOLE_ON) console.log(`${Emojis.warning} Unknown type -> worker %c ${e.data.type} %c`, Cls.Cri, '');
      }
    };
  } else {
    const infoString = `UNSUPPORTED BROWSER INFO
      appName: %c ${window.navigator.appName} %c
      appVersion: %c ${window.navigator.appVersion} %c
      userAgent: : %c  ${window.navigator.userAgent} %c
    `;
    const infoMsg = 'Unable to spin up web worked, browser does not support it';
    const msg = `${Emojis.warning} %c ERROR %c ${infoMsg} ${infoString}`;
    console.log(msg, Cls.Cri, '', Cls.Army, '', Cls.Val, '', Cls.Info, '');
  }
};

export const appUiSlice = createSlice({
  name: 'app.ui',
  initialState,
  reducers: {
    setShowAuthentication: (state, action: PayloadAction<boolean>) => {
      state.showAuthentication = action.payload;
    },
    setShowUILibrary: (state, action: PayloadAction<boolean>) => {
      state.showUILibrary = action.payload;
    },
    setModalVisible: (state, action: PayloadAction<boolean>) => {
      state.modalVisible = action.payload;
    },
    setShowStarterScreen: (state, action: PayloadAction<boolean>) => {
      state.showStarterScreen = action.payload;
    },
    setShowBackButton: (state, action: PayloadAction<boolean>) => {
      state.showBackButton = action.payload;
    },
    setRouteToLoadAfterAuth: (state, action: PayloadAction<string>) => {
      state.routeToLoadAfterAuth = action.payload;
    },
    setIndexDBInitialized: (state, action: PayloadAction<boolean>) => {
      state.isIndexDBInitialized = action.payload;
    },
    setDisplayCode: (state, action: PayloadAction<boolean>) => {
      state.displayCode = action.payload;
    },
    setShowLoader: (state, action: PayloadAction<LoaderDetails>) => {
      state.loader = { ...state.loader, ...action.payload };
    },
    setAdminAction: (state, action: PayloadAction<AdminActionWithAuditLogState>) => {
      state.admin.adminAction = {
        ...state.admin.adminAction,
        ...action.payload,
        week: action.payload.week === 'all' ? GameWeek.Week1 : action.payload.week,
      };
    },
    setShowAdminActions: (state, action: PayloadAction<boolean>) => {
      state.showAdminActions = action.payload;
    },
    setGameIDsMap: (state, action: PayloadAction<Record<SeasonID, SeasonalGameIDMap>>) => {
      state.admin.gameIDsMap = action.payload;
    },
    setWeeklyGameIDsMap: (state, action: PayloadAction<WeeklyGameIDsPayload>) => {
      const { yearID, seasonWeekID, gameIDs } = action.payload;
      state.admin.gameIDsMap[yearID] = {
        ...state.admin.gameIDsMap[yearID],
        [seasonWeekID]: gameIDs,
      };
    },
    setApplicationInitialized: (state, action: PayloadAction<boolean>) => {
      state.applicationInitialized = action.payload;
    },
    setWebWorker: (state, action: PayloadAction<Partial<WebWorkerType>>) => {
      state.webworker = { ...state.webworker, ...action.payload };
    },
    setShowTimeNavigator: (state, action: PayloadAction<boolean>) => {
      state.showTimeNavigator = action.payload;
    },
    setSelectedGameCategoryType: (state, action: PayloadAction<CategoryRankingsNavBarState>) => {
      state.categoryRankingsNavBarState = action.payload;
    },
    resetAppUi: () => {
      return { ...initialState };
    },
  },
});

export const {
  setShowAuthentication,
  setShowUILibrary,
  setModalVisible,
  setShowStarterScreen,
  setShowBackButton,
  setRouteToLoadAfterAuth,
  setIndexDBInitialized,
  setDisplayCode,
  setShowLoader,
  setAdminAction,
  setShowAdminActions,
  setApplicationInitialized,
  setWebWorker,
  setShowTimeNavigator,
  setSelectedGameCategoryType,
  resetAppUi,
} = appUiSlice.actions;

export const getShowUILibrary = (state: RootState): boolean => state.ui.app.showUILibrary;
export const getModalVisible = (state: RootState): boolean => state.ui.app.modalVisible;
// export const getSelectedFilters = (state: RootState): SelectedFilter[] => state.ui.app.selectedFilters;
export const getDisplayCode = (state: RootState): boolean => state.ui.app.displayCode;
export const getLoader = (state: RootState): LoaderDetails => state.ui.app.loader;
export const getShowLoader = (state: RootState): boolean => state.ui.app.loader.visible;
export const getShowStarterScreen = (state: RootState): boolean => state.ui.app.showStarterScreen;
export const getRouteToLoadAfterAuth = (state: RootState): string => state.ui.app.routeToLoadAfterAuth;
export const getShowBackButton = (state: RootState): boolean => state.ui.app.showBackButton;

export const getAdminAction = (state: RootState): AdminActionWithAuditLogState => state.ui.app.admin.adminAction;
export const getShowAdminActions = (state: RootState): boolean => state.ui.app.showAdminActions;
export const getApplicationInitialized = (state: RootState): boolean => state.ui.app.applicationInitialized;
export const getShowAuthentication = (state: RootState): boolean => {
  const userAuthState = state.user.authState;
  return (
    userAuthState === UserAuthState.LoggedOut ||
    userAuthState === UserAuthState.NotLoggedIn ||
    userAuthState === UserAuthState.Error
  );
};
export const getWebWorkerType = (state: RootState): WebWorkerType => state.ui.app.webworker;
export const getIsIndexDBInitialized = (state: RootState): boolean => state.ui.app.isIndexDBInitialized;

export const getShowTimeNavigator = (state: RootState): boolean => state.ui.app.showTimeNavigator;
export const getCategoryRankingsNavBarState = (state: RootState): CategoryRankingsNavBarState =>
  state.ui.app.categoryRankingsNavBarState;
export const getCategoryFromNavBarState = (state: RootState): StatCategoryAbrv =>
  state.ui.app.categoryRankingsNavBarState.selectedCategory;
export const getCategoryGroupFromNavBarState = (state: RootState): CategoryGroupName =>
  state.ui.app.categoryRankingsNavBarState.selectedGroup;
export const getCategoryKindFromNavBarState = (state: RootState): CategoryRankingsKindType =>
  state.ui.app.categoryRankingsNavBarState.selectedKind;

export default appUiSlice.reducer;
